import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { devRequest } from "../admin/api";
import { SignIn, useDevSession } from "./GetKey";

const RESULT_TEXT = { risky: "Risky change", malicious: "Confirmed malicious", changed: "New version" };

export default function Watchlist() {
  const { loading, me, err, reload, signOut } = useDevSession();
  const [data, setData] = useState(null);
  const [input, setInput] = useState("");
  const [result, setResult] = useState(null);
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    try { setData(await devRequest("/public/dev/watchlist")); } catch (ex) { setMsg(ex.message); }
  }, []);
  useEffect(() => { if (me) load(); }, [me, load]);

  const add = async (e) => {
    e.preventDefault(); setMsg(""); setResult(null);
    try { setResult(await devRequest("/public/dev/watchlist", { method: "POST", body: { input } })); setInput(""); load(); reload(); }
    catch (ex) { setMsg(ex.message); }
  };
  const remove = async (id) => {
    try { await devRequest(`/public/dev/watchlist/${id}`, { method: "DELETE" }); load(); } catch (ex) { setMsg(ex.message); }
  };
  const toggleAlerts = async () => {
    try { await devRequest("/public/dev/me", { method: "PATCH", body: { watch_alerts: !data.alerts_on } }); load(); }
    catch (ex) { setMsg(ex.message); }
  };

  return (
    <section data-testid="watchlist-page" className="space-y-6">
      <h1 className="hs-display text-3xl md:text-4xl">Watchlist</h1>
      <p className="text-muted hs-prose">
        Get an email when a server you use changes risk: a new version that adds a capability, contacts a new domain,
        adds an install script, or adds text aimed at the AI model. New versions without those changes do not email you.
      </p>
      {err && <p role="alert" className="hs-badge hs-badge-danger">{err}</p>}
      {loading ? <p className="text-muted">Loading…</p> : !me ? <SignIn next="watchlist" /> : (
        <div className="space-y-4">
          <div className="hs-card max-w-3xl" data-testid="dev-account">
            Signed in as <strong>{me.user.email}</strong>. <Link className="hs-link" to="/get-key">API keys</Link> ·{" "}
            <button type="button" className="hs-link" onClick={signOut}>Sign out</button>
          </div>
          <form onSubmit={add} className="hs-card max-w-3xl space-y-3" data-testid="watch-form">
            <label className="hs-label" htmlFor="watch-input">Servers to watch</label>
            <textarea id="watch-input" data-testid="watch-input" className="hs-input" rows={5} maxLength={50000} value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={"One per line: a registry name (io.github.owner/server), npm:package, pypi:package or an https URL.\nOr paste the output of: npx herdsafe scan --json"} />
            <button type="submit" className="hs-btn hs-btn-primary" disabled={!input.trim()} data-testid="watch-add">Add</button>
            {result && (
              <p className="text-sm" role="status" data-testid="watch-result">
                Added {result.added.length}.
                {result.already_watched.length > 0 && ` Already watched: ${result.already_watched.join(", ")}.`}
                {result.not_found.length > 0 && ` Not in HerdSafe yet: ${result.not_found.join(", ")}.`}
              </p>
            )}
            {msg && <p role="alert" className="hs-badge hs-badge-danger">{msg}</p>}
          </form>
          {data && (
            <div className="hs-card max-w-3xl space-y-3">
              <div className="flex flex-wrap items-center gap-3">
                <h2 className="hs-display text-xl m-0">Watching {data.watches.length}</h2>
                <span className={`hs-badge ${data.alerts_on ? "hs-badge-safe" : "hs-badge-neutral"}`}>
                  Emails {data.alerts_on ? "on" : "off"}</span>
                <button type="button" className="hs-btn" onClick={toggleAlerts} data-testid="toggle-alerts">
                  {data.alerts_on ? "Turn emails off" : "Turn emails on"}</button>
              </div>
              <ul className="space-y-3" data-testid="watch-list">
                {data.watches.map((w) => (
                  <li key={w.id} className="flex flex-wrap items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div><strong>{w.name}</strong>{w.label !== w.name && <span className="text-muted"> ({w.label})</span>}</div>
                      {w.status === "malicious" && <span className="hs-badge hs-badge-danger">Confirmed malicious</span>}
                      {w.last_change && (
                        <div className="text-sm text-muted">
                          {RESULT_TEXT[w.last_change.result] || "Changed"} on {w.last_change.at.slice(0, 10)}: {w.last_change.changes.join("; ")}
                        </div>
                      )}
                    </div>
                    <button type="button" className="hs-btn" onClick={() => remove(w.id)}>Remove</button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
